/**
 * Public-website profile API client (round-22 Phase D — realigned
 * to the shipped Smithy contract).
 *
 * Wraps the two `ProfileApi` v1 operations `GetProfileV1` and
 * `UpsertProfileV1` from `models/smithy/profile-api.smithy`
 * (round-22 Phase C). Every request is authenticated — the public
 * REST API gateway's Cognito authorizer keys on the ID token in
 * `Authorization: Bearer <token>`. The caller (typically a thunk
 * reading the auth slice or `<OAuthCallbackPage>` reading the
 * freshly-exchanged token) threads the JWT in via the `idToken`
 * argument.
 *
 * **SWR cache bypass.** Profile data is user-specific and must
 * always reflect the most recent server state. This client calls
 * `globalThis.fetch` directly — not the SWR wrapper — so the
 * memory + sessionStorage caches never see the response. A
 * matching CloudFront cache behavior with `cachePolicy =
 * CACHE_DISABLED` mirrors the bypass at the edge.
 *
 * **No `confirmEmail` in this build.** The pre-round-22 SPA had a
 * placeholder `POST /Profile/ConfirmEmailV1` surface; the shipped
 * Smithy contract does not have such an operation. Email
 * verification runs entirely through Cognito's own confirm-signup
 * flow (`<VerifyEmailPage>` in the SPA + Cognito's SES-hosted
 * verification email); the profile row's `emailConfirmed` field is
 * populated by the backend from the JWT's `email_verified` claim.
 *
 * Function-length budget: every public export stays under 20
 * lines per Requirement 3.7.
 */

import { getPublicWebsiteConfig } from './config';

/**
 * Full profile record returned by `GetProfileV1` / `UpsertProfileV1`
 * — mirrors `Profile` in the Smithy IDL. Every field is populated
 * server-side; the SPA treats it as read-only.
 */
export interface ProfileResponse {
  readonly cognitoSub: string;
  readonly username: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly emailConfirmed: boolean;
  readonly phoneE164?: string;
  readonly dateOfBirth?: string;
  readonly preferredPlatforms?: ReadonlyArray<string>;
  readonly optInNewsletter: boolean;
  readonly optInPaidFeatures: boolean;
  readonly created: string;
  readonly updated: string;
  readonly version: number;
}

/**
 * Input for `UpsertProfileV1`. `username`, `firstName`,
 * `lastName` are required on first upsert (and remain required
 * on every subsequent upsert — the backend re-validates the
 * gamertag-uniqueness invariant every call). `email` is NOT
 * settable — it comes from the JWT.
 */
export interface UpsertProfileInput {
  readonly username: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phoneE164?: string;
  readonly dateOfBirth?: string;
  readonly preferredPlatforms?: ReadonlyArray<string>;
  readonly optInNewsletter?: boolean;
  readonly optInPaidFeatures?: boolean;
}

/** Profile-existence result returned by {@link getProfile}. */
export interface GetProfileResult {
  readonly exists: boolean;
  readonly profile?: ProfileResponse;
}

function profileHeaders(idToken: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
}

interface GetProfileV1Response {
  readonly profile?: ProfileResponse;
}

interface UpsertProfileV1Response {
  readonly profile?: ProfileResponse;
}

/**
 * Fetch the caller's profile record. Used by `<OAuthCallbackPage>`
 * to branch between `/` (returning user) and `/sign-up`
 * (first-time user).
 *
 * @param idToken Cognito ID token from the auth slice.
 * @returns `{ exists: true, profile }` on 200; `{ exists: false }`
 *   on 404. Throws on any other non-2xx.
 */
export async function getProfile(idToken: string): Promise<GetProfileResult> {
  const { publicApiInvokeUrl } = getPublicWebsiteConfig();
  const response = await fetch(`${publicApiInvokeUrl}/GetProfileV1`, {
    method: 'POST',
    headers: profileHeaders(idToken),
    body: '{}',
  });
  if (response.status === 404) return { exists: false };
  if (!response.ok) throw new Error(`getProfile-failed-${response.status}`);
  const body = (await response.json()) as GetProfileV1Response;
  if (body.profile === undefined) return { exists: false };
  return { exists: true, profile: body.profile };
}

/**
 * Create or update the caller's profile record. Used by
 * `<SignUpPage>` (initial profile row after email verification),
 * `<ProfilePage>` (edits), and `<NewsletterPage>` (newsletter
 * opt-in toggle).
 *
 * @param input Full profile fields to upsert.
 * @param idToken Cognito ID token.
 * @returns The full upserted profile record. Throws on non-2xx;
 *   the caller inspects the thrown message for `409` / `Username`
 *   to surface a "username taken" message.
 */
export async function upsertProfile(
  input: UpsertProfileInput,
  idToken: string,
): Promise<ProfileResponse> {
  const { publicApiInvokeUrl } = getPublicWebsiteConfig();
  const response = await fetch(`${publicApiInvokeUrl}/UpsertProfileV1`, {
    method: 'POST',
    headers: profileHeaders(idToken),
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`upsertProfile-failed-${response.status}`);
  const body = (await response.json()) as UpsertProfileV1Response;
  if (body.profile === undefined) throw new Error('upsertProfile-empty-response');
  return body.profile;
}
